"use client"

import { ControlledModal, toast } from "@/components"
import { TextArea } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { UpdateCreativeBioDocument } from "@/gql/graphql"
import { useMutation } from "@apollo/client"
import { captureException } from "@sentry/nextjs"
import { FC, useEffect, useMemo, useState } from "react"

const EditDescriptionModalFragment = graphql(`
  fragment EditDescriptionModal on IndividualCreative {
    id
    __typename
    bio
  }
`)

interface Props {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  creative: FragmentType<typeof EditDescriptionModalFragment> | null | undefined
}

graphql(`
  mutation UpdateCreativeBio($input: UpdateCreativeBioInput!) {
    updateCreativeBio(input: $input) {
      errors {
        field
        messages
      }
      creative {
        __typename
        id
        bio
      }
    }
  }
`)

export const EditDescriptionModal: FC<Props> = ({ isOpen, onOpenChange, ...props }) => {
  const creative = useFragment(EditDescriptionModalFragment, props.creative)
  const [loading, setLoading] = useState(false)
  const [update] = useMutation(UpdateCreativeBioDocument)
  const [value, setValue] = useState(creative?.bio || "")

  useEffect(() => {
    setValue(creative?.bio || "")
  }, [creative?.bio])

  const onUpdate = async () => {
    if (loading) return
    setLoading(true)
    const response = await update({
      variables: {
        input: {
          id: creative?.id!,
          bio: value,
        },
      },
    })
    const errors = response?.data?.updateCreativeBio?.errors
    if (errors && errors.length > 0) {
      captureException(errors)
      toast.error("Error updating description")
    } else {
      onOpenChange(false)
    }

    setLoading(false)
  }

  const textArea = useMemo(
    () => (
      <TextArea
        label="About"
        className="h-[170px] w-full"
        tabIndex={0}
        value={value}
        onChange={e => {
          setValue(e.target.value)
        }}
      />
    ),
    [value]
  )

  return (
    <ControlledModal
      open={isOpen}
      onOpenChange={onOpenChange}
      header={{ title: "Update your description", variant: "default" }}
      body={textArea}
      footer={{
        fixed: true,
        tertiaryButton: {
          children: "Cancel",
          onClick: () => onOpenChange(false),
        },
        primaryButton: {
          children: "Update",
          onClick: onUpdate,
          loading: loading,
        },
      }}
    />
  )
}
